import type { ChangeEventHandler, ReactNode } from 'react';

import {
  Button,
  Field,
  FieldError,
  FieldLabel,
  Input,
  Marker,
  MarkerContent,
  Textarea
} from 'design-system';
import { Link, Mail } from 'lucide-react';

import copyToClipboard from '@/shared/lib/copyToClipboard';

export type ShareDialogContentProps = {
  header: ReactNode;
  showHeader?: boolean;
  shareUrl?: string;
  emailError: string;
  recipient: string;
  content: string;
  enableSendEmail?: boolean;
  renderLinkSection?: ReactNode;
  onCopyClick?: () => void;
  handleChangeRecipient: ChangeEventHandler<HTMLInputElement>;
  onContentChange: ChangeEventHandler<HTMLTextAreaElement>;
  onSendEmail: () => void;
};

/**
 * Reusable sharing form content for a dialog. It deliberately does not render
 * a dialog title so it can work inside both legacy and design-system dialogs.
 */
const ShareDialogContent = ({
  header,
  showHeader = true,
  shareUrl,
  emailError,
  recipient,
  content,
  enableSendEmail = true,
  renderLinkSection,
  onCopyClick,
  handleChangeRecipient,
  onContentChange,
  onSendEmail
}: ShareDialogContentProps) => {
  const handleCopyClick =
    onCopyClick ?? (() => copyToClipboard(shareUrl ?? '', 'Link copied to clipboard!'));

  return (
    <div className='flex w-full min-w-0 max-w-full flex-col gap-5'>
      {showHeader &&
        (typeof header === 'string' ? (
          <header>
            <h2 className='text-lg font-semibold'>{header}</h2>
          </header>
        ) : (
          header
        ))}

      {renderLinkSection ?? (
        <section
          aria-labelledby='share-link-heading'
          className='flex flex-col gap-3'
        >
          <h3
            id='share-link-heading'
            className='flex items-center gap-2 text-sm font-medium'
          >
            <Link
              aria-hidden='true'
              className='size-4'
            />
            Share a link
          </h3>
          <div className='flex flex-col gap-2 sm:flex-row'>
            <Field className='min-w-0 flex-1 gap-1'>
              <FieldLabel htmlFor='share-link'>Share link</FieldLabel>
              <Input
                id='share-link'
                autoComplete='off'
                readOnly
                value={shareUrl ?? ''}
              />
            </Field>
            <Button
              className='shrink-0 self-end'
              size='lg'
              type='button'
              onClick={handleCopyClick}
            >
              <Link
                aria-hidden='true'
                data-icon='inline-start'
              />
              Copy link
            </Button>
          </div>
        </section>
      )}

      {enableSendEmail ? (
        <>
          <Marker
            aria-label='or'
            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role -- hr can't hold the "or" label; Marker needs role="separator" for a11y instead.
            role='separator'
            variant='separator'
          >
            <MarkerContent aria-hidden='true'>or</MarkerContent>
          </Marker>
          <section
            aria-labelledby='share-email-heading'
            className='flex flex-col gap-3'
          >
            <h3
              id='share-email-heading'
              className='flex items-center gap-2 text-sm font-medium'
            >
              <Mail
                aria-hidden='true'
                className='size-4'
              />
              Share via email
            </h3>
            <div className='flex flex-col gap-2 sm:flex-row'>
              <Field
                className='min-w-0 flex-1 gap-1'
                data-invalid={emailError ? 'true' : undefined}
              >
                <FieldLabel htmlFor='recipient-email'>Recipient email</FieldLabel>
                <Input
                  id='recipient-email'
                  aria-invalid={emailError ? true : undefined}
                  placeholder="Enter the recipient's email..."
                  type='email'
                  value={recipient}
                  onChange={handleChangeRecipient}
                />
                <FieldError>{emailError}</FieldError>
              </Field>
              <Button
                className='shrink-0 self-end'
                size='lg'
                type='button'
                onClick={onSendEmail}
              >
                <Mail
                  aria-hidden='true'
                  data-icon='inline-start'
                />
                Send an email
              </Button>
            </div>
            <Field className='gap-1'>
              <FieldLabel htmlFor='share-message'>Optional message</FieldLabel>
              <Textarea
                id='share-message'
                placeholder='Type your message...'
                rows={3}
                value={content}
                onChange={onContentChange}
              />
            </Field>
          </section>
        </>
      ) : null}
    </div>
  );
};

export default ShareDialogContent;
